(function () {
  'use strict';

  const SYNC_DELAY_MS = 350;
  const FETCH_COOLDOWN_MS = 750;
  const BSS_WAIT_MS = 8000;
  const OBSERVER_REPAIR_COOLDOWN_MS = 150;
  const ASSERT_WAVE_MS = [0, 750, 1800];
  const formatterCache = new Map();
  const NUMERIC_PART_TYPES = new Set(['integer', 'group', 'decimal', 'fraction', 'minusSign']);
  const PROMO_GIFT_PROP = '_promo_gift';

  let syncTimer = null;
  let pendingCartPayload = null;
  let lastCartPayload = null;
  let cachedRowMap = null;
  let rowMapDirty = true;
  let cachedTotalEls = null;
  let forceNextSync = false;
  let isSyncing = false;
  let suppressObserver = false;
  let lastCartFingerprint = null;
  let lastScheduledKey = null;
  let lastFetchTime = 0;
  let cartFetchPromise = null;
  let lastCurrency = '';
  let lastObserverRepairAt = 0;
  let assertWaveTimers = [];
  let initialized = false;

  function isFullCartPayload(cart) {
    // /cart/add.js returns { items: [...] } for added lines only — require a real cart.
    return Boolean(
      cart &&
        Array.isArray(cart.items) &&
        typeof cart.token === 'string' &&
        cart.token &&
        typeof cart.total_price === 'number'
    );
  }

  function getItemProperties(item) {
    const p = item && item.properties;
    if (!p) return {};
    if (Array.isArray(p)) {
      const o = {};
      p.forEach((row) => {
        if (row && row.name) o[String(row.name)] = row.value;
      });
      return o;
    }
    return typeof p === 'object' ? p : {};
  }

  function cartHasPromoGift(cart) {
    if (!isFullCartPayload(cart)) return false;
    return cart.items.some((item) => {
      const props = getItemProperties(item);
      return props[PROMO_GIFT_PROP] === true || props[PROMO_GIFT_PROP] === 'true';
    });
  }

  function cartHasShopifyDiscount(cart) {
    if (!isFullCartPayload(cart)) return false;
    if (Number(cart.total_discount) > 0) return true;
    if (cartHasPromoGift(cart)) return true;
    if (
      Array.isArray(cart.cart_level_discount_applications) &&
      cart.cart_level_discount_applications.length
    ) {
      return true;
    }
    return cart.items.some(
      (item) => Number(item.total_discount) > 0 && item.original_price > item.final_price
    );
  }

  function shouldRepairTotals(cart) {
    if (!isFullCartPayload(cart)) return false;
    if (cartHasShopifyDiscount(cart)) return true;
    // Liquid may stamp discount metadata even before JS sees applications.
    const els = getTotalEls();
    for (let i = 0; i < els.length; i += 1) {
      const discountAttr = Number(els[i].getAttribute('data-shopify-total-discount') || 0);
      if (discountAttr > 0) return true;
    }
    return false;
  }

  function getCartFingerprint(cart) {
    if (!isFullCartPayload(cart)) return null;

    const itemFingerprint = cart.items
      .map((item) => `${item.key}:${item.quantity}:${item.final_price}:${item.total_discount}`)
      .join('|');

    return `${cart.currency || ''}-${cart.item_count}-${cart.total_price}-${cart.total_discount}-${itemFingerprint}`;
  }

  function getCartQueryRoots() {
    const roots = [];
    const cartPage = document.querySelector('.main-cart-container');
    const drawerInner = document.querySelector('cart-drawer .drawer__inner');

    if (cartPage) roots.push(cartPage);
    if (drawerInner) roots.push(drawerInner);

    return roots.length ? roots : [document];
  }

  function invalidateDomCaches() {
    rowMapDirty = true;
    cachedRowMap = null;
    cachedTotalEls = null;
  }

  function invalidateContentCaches() {
    cachedTotalEls = null;
  }

  function getRowMap() {
    if (!rowMapDirty && cachedRowMap) return cachedRowMap;

    cachedRowMap = new Map();
    getCartQueryRoots().forEach((root) => {
      root.querySelectorAll('[bss-b2b-cart-item-key]').forEach((row) => {
        const key = row.getAttribute('bss-b2b-cart-item-key');
        if (!key) return;

        const rows = cachedRowMap.get(key);
        if (rows) {
          rows.push(row);
        } else {
          cachedRowMap.set(key, [row]);
        }
      });
    });
    rowMapDirty = false;
    return cachedRowMap;
  }

  function getTotalEls() {
    if (
      !cachedTotalEls ||
      cachedTotalEls.length === 0 ||
      !document.body.contains(cachedTotalEls[0])
    ) {
      cachedTotalEls = document.querySelectorAll('[bss-b2b-cart-total-price]');
    }
    return cachedTotalEls;
  }

  function resolveSyncForce(options) {
    if (!options) return false;
    return Boolean(options.forceFromDom || options.forceFromState || options.forceTotal);
  }

  function getFormatter(currencyCode) {
    const code = String(currencyCode || '').trim() || 'USD';
    if (formatterCache.has(code)) return formatterCache.get(code);

    try {
      const formatter = new Intl.NumberFormat(undefined, { style: 'currency', currency: code });
      formatterCache.set(code, formatter);
      return formatter;
    } catch (e) {
      formatterCache.set(code, null);
      return null;
    }
  }

  function formatMoneyParts(cents, currencyCode) {
    const code = String(currencyCode || '').trim() || 'USD';
    const formatter = getFormatter(code);
    const value = cents / 100;

    if (!formatter) {
      const amount = value.toFixed(2);
      return {
        formatted: `$${amount}`,
        currency: '$',
        amount: `$${amount}`,
      };
    }

    const parts = formatter.formatToParts(value);
    let currency = '';
    let amount = '';
    let seenNumber = false;

    parts.forEach((part) => {
      if (part.type === 'currency') {
        currency += part.value;
        return;
      }
      if (NUMERIC_PART_TYPES.has(part.type)) {
        amount += part.value;
        seenNumber = true;
        return;
      }
      if (part.type === 'literal' && seenNumber) {
        amount += part.value;
      }
    });

    // Theme Liquid puts money (incl. symbol) in .amount and currency code in .currency.
    const amountWithSymbol = currency ? `${currency}${amount}` : formatter.format(value);

    return {
      formatted: formatter.format(value),
      currency,
      amount: amountWithSymbol,
    };
  }

  function isBssContext(el) {
    if (!el || typeof el.closest !== 'function') return false;

    return Boolean(
      el.closest(
        '[bss-b2b-cart-item-key], [bss-b2b-cart-total-price], [bss-b2b-item-original-price], [data-shopify-discount-key]'
      )
    );
  }

  function mutationsAffectBss(mutations) {
    return mutations.some((mutation) => {
      if (mutation.type === 'characterData') {
        return isBssContext(mutation.target.parentElement);
      }

      if (
        mutation.type === 'childList' &&
        (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0)
      ) {
        if (isBssContext(mutation.target)) return true;

        for (let i = 0; i < mutation.addedNodes.length; i += 1) {
          const node = mutation.addedNodes[i];
          if (node.nodeType === Node.ELEMENT_NODE && isBssContext(node)) return true;
        }

        for (let j = 0; j < mutation.removedNodes.length; j += 1) {
          const node = mutation.removedNodes[j];
          if (node.nodeType === Node.ELEMENT_NODE && isBssContext(node)) return true;
        }
      }

      return false;
    });
  }

  function getPayableTotal(cart) {
    if (!cart) return 0;
    return Number(cart.total_price) || 0;
  }

  function parseDisplayedCents(text) {
    if (!text) return null;
    const cleaned = String(text).replace(/[^0-9.]/g, '');
    if (!cleaned) return null;
    const value = parseFloat(cleaned);
    if (Number.isNaN(value)) return null;
    return Math.round(value * 100);
  }

  function totalDomMatchesPayable(cents) {
    const els = getTotalEls();
    if (!els.length) return true;

    const expected = Number(cents);

    for (let i = 0; i < els.length; i += 1) {
      const el = els[i];
      const marked = el.getAttribute('data-qc-payable-total');
      const amountEl = el.querySelector('.amount');
      const displayed = parseDisplayedCents(amountEl ? amountEl.textContent : el.textContent);

      if (marked != null && Number(marked) === expected && displayed === expected) {
        continue;
      }

      if (displayed !== expected) return false;
    }
    return true;
  }

  function releaseObserverSoon() {
    setTimeout(() => {
      suppressObserver = false;
    }, 0);
  }

  function updateTotalElement(totalEl, cents, currency) {
    if (!totalEl) return;

    const parts = formatMoneyParts(cents, currency);
    const amountEl = totalEl.querySelector('.amount');

    suppressObserver = true;
    try {
      if (amountEl) {
        if (amountEl.textContent !== parts.amount) {
          amountEl.textContent = parts.amount;
        }
      } else {
        const currencyEl = totalEl.querySelector('.currency');
        const bssCurrency = totalEl.querySelector('.bss-currency');
        if (currencyEl || bssCurrency) {
          let span = totalEl.querySelector('.amount');
          if (!span) {
            span = document.createElement('span');
            span.className = 'amount';
            totalEl.appendChild(span);
          }
          span.textContent = parts.amount;
        } else if (totalEl.textContent !== parts.formatted) {
          totalEl.textContent = parts.formatted;
        }
      }
      totalEl.setAttribute('data-qc-payable-total', String(cents));
      totalEl.setAttribute('data-shopify-original-total', String(cents));
    } finally {
      releaseObserverSoon();
    }
  }

  function applyPayableTotalToDom(cart) {
    if (!shouldRepairTotals(cart)) return false;

    const payableTotal = getPayableTotal(cart);
    const currency = cart.currency || lastCurrency;

    if (totalDomMatchesPayable(payableTotal)) {
      if (currency) lastCurrency = currency;
      return false;
    }

    if (currency) lastCurrency = currency;

    getTotalEls().forEach((totalEl) => {
      updateTotalElement(totalEl, payableTotal, currency);
    });
    return true;
  }

  function applyDiscountToRow(row) {
    const discountEl = row.querySelector('[data-shopify-discount-key]');
    if (!discountEl) return;

    const bssFallback = row.querySelector('.cart-bss-price-fallback');
    discountEl.hidden = false;
    discountEl.style.display = '';

    if (bssFallback) {
      bssFallback.setAttribute('aria-hidden', 'true');
    }

    const bssPrimary = row.hasAttribute('bss-b2b-item-original-price')
      ? row
      : row.querySelector('[bss-b2b-item-original-price]:not(.cart-bss-price-fallback)');

    if (bssPrimary && bssPrimary !== bssFallback) {
      bssPrimary.classList.add('cart-bss-price-fallback', 'visually-hidden');
      bssPrimary.setAttribute('aria-hidden', 'true');
    }
  }

  function applyLineDiscountVisibility(cart) {
    if (!isFullCartPayload(cart)) return;

    const rowMap = getRowMap();

    cart.items.forEach((item) => {
      const hasDiscount = item.total_discount > 0 && item.original_price > item.final_price;
      const rows = rowMap.get(item.key);
      if (!rows || !rows.length || !hasDiscount) return;

      rows.forEach((row) => {
        applyDiscountToRow(row);
      });
    });
  }

  function syncFromCart(cart, options) {
    if (!isFullCartPayload(cart)) return false;

    const force = resolveSyncForce(options);
    const fingerprint = getCartFingerprint(cart);
    if (!force && fingerprint === lastCartFingerprint) {
      applyPayableTotalToDom(cart);
      return false;
    }

    isSyncing = true;
    suppressObserver = true;
    try {
      lastCartPayload = cart;
      applyLineDiscountVisibility(cart);
      applyPayableTotalToDom(cart);
      lastCartFingerprint = fingerprint;
      return true;
    } finally {
      isSyncing = false;
      releaseObserverSoon();
    }
  }

  function fetchCartAndSync(forceOptions) {
    const options = forceOptions || {};
    const now = Date.now();
    if (
      !options.forceFreshCart &&
      now - lastFetchTime < FETCH_COOLDOWN_MS &&
      isFullCartPayload(lastCartPayload)
    ) {
      syncFromCart(lastCartPayload, Object.assign({ forceTotal: true }, options));
      // Skip duplicate requests while a recent authoritative cart state exists.
      return Promise.resolve(lastCartPayload);
    }

    if (cartFetchPromise) {
      return cartFetchPromise.then((cart) => {
        if (isFullCartPayload(cart)) {
          syncFromCart(cart, options);
        }
        return cart;
      });
    }

    lastFetchTime = now;
    cartFetchPromise = fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : null))
      .then((cart) => {
        if (isFullCartPayload(cart)) {
          syncFromCart(cart, options);
        }
        return cart;
      })
      .catch(() => null)
      .finally(() => {
        cartFetchPromise = null;
      });

    return cartFetchPromise;
  }

  function clearAssertWaves() {
    assertWaveTimers.forEach((id) => clearTimeout(id));
    assertWaveTimers = [];
  }

  function reassertOrFetch() {
    invalidateContentCaches();

    if (isFullCartPayload(lastCartPayload)) {
      applyPayableTotalToDom(lastCartPayload);
      return;
    }

    fetchCartAndSync({ forceFromState: true, forceTotal: true, forceFromDom: true });
  }

  function scheduleAssertWaves() {
    clearAssertWaves();

    // BSS often overwrites the total after our first repair. Re-assert across
    // delayed waves using cached cart state; fetch only when none exists.
    ASSERT_WAVE_MS.forEach((delay, index) => {
      const id = setTimeout(() => {
        if (index === 0) invalidateDomCaches();
        reassertOrFetch();
      }, delay);
      assertWaveTimers.push(id);
    });
  }

  function runScheduledSync() {
    syncTimer = null;
    lastScheduledKey = null;

    const forceFromDom = rowMapDirty;
    const forceFromState = forceNextSync;
    forceNextSync = false;

    const forceOptions = {
      forceFromDom,
      forceFromState,
      forceTotal: true,
    };

    const cart = isFullCartPayload(pendingCartPayload)
      ? pendingCartPayload
      : !forceFromState && isFullCartPayload(lastCartPayload)
        ? lastCartPayload
        : null;
    pendingCartPayload = null;

    if (cart) {
      syncFromCart(cart, forceOptions);
      scheduleAssertWaves();
      return;
    }

    fetchCartAndSync(
      Object.assign({ forceFreshCart: true }, forceOptions)
    ).then(() => {
      scheduleAssertWaves();
    });
  }

  function markDomChanged(reason) {
    if (reason === 'cart' || reason === 'structural') {
      forceNextSync = true;
      invalidateDomCaches();
      return;
    }

    if (reason === 'content' || reason === 'bss') {
      forceNextSync = true;
      invalidateContentCaches();
    }
  }

  function scheduleSync(cartHint) {
    if (isFullCartPayload(cartHint)) {
      pendingCartPayload = cartHint;
      const fingerprint = getCartFingerprint(cartHint);
      if (fingerprint !== lastCartFingerprint) {
        markDomChanged('cart');
      } else {
        applyPayableTotalToDom(cartHint);
      }
    } else {
      // Partial /cart/add.js payloads must never become lastCartPayload.
      markDomChanged('cart');
    }

    const fp = pendingCartPayload ? getCartFingerprint(pendingCartPayload) : null;
    const scheduleKey = fp ? `cart:${fp}` : 'dom:unknown';
    if (scheduleKey === lastScheduledKey && syncTimer) return;
    lastScheduledKey = scheduleKey;

    clearTimeout(syncTimer);
    syncTimer = setTimeout(runScheduledSync, SYNC_DELAY_MS);
  }

  function onBssCartUpdate() {
    if (isFullCartPayload(lastCartPayload)) {
      applyPayableTotalToDom(lastCartPayload);
      scheduleAssertWaves();
      return;
    }
    scheduleSync(null);
  }

  function shouldEnableRepair() {
    const cfg = window.qcCartDiscountRepair || {};
    if (cfg.forceEnable) return true;
    if (window.b2bToken) return true;
    if (window.BSS_B2B) return true;
    if (document.querySelector('[bss-b2b-cart-total-price]')) return true;
    return false;
  }

  function init() {
    if (initialized) return;
    initialized = true;

    scheduleSync(null);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const cartHint = event && isFullCartPayload(event.cartData) ? event.cartData : null;
        scheduleSync(cartHint);
      });
    }

    document.addEventListener('cart:updated', (event) => {
      const cartHint =
        event && event.detail && isFullCartPayload(event.detail.cart) ? event.detail.cart : null;
      scheduleSync(cartHint);
    });

    document.addEventListener('bss_b2b:CustomCartUpdate', onBssCartUpdate);

    const observeTargets = [];
    const cartDrawer = document.querySelector('cart-drawer');
    const drawerTarget =
      cartDrawer &&
      (cartDrawer.querySelector('.drawer__inner') || cartDrawer.querySelector('cart-drawer-items'));
    const cartPageTarget = document.querySelector('.main-cart-container');

    if (drawerTarget) observeTargets.push(drawerTarget);
    if (cartPageTarget) observeTargets.push(cartPageTarget);

    if (observeTargets.length && typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver((mutations) => {
        if (isSyncing || suppressObserver) return;
        if (!mutationsAffectBss(mutations)) return;

        const now = Date.now();
        if (now - lastObserverRepairAt < OBSERVER_REPAIR_COOLDOWN_MS) return;
        lastObserverRepairAt = now;

        invalidateContentCaches();

        if (isFullCartPayload(lastCartPayload) && shouldRepairTotals(lastCartPayload)) {
          applyPayableTotalToDom(lastCartPayload);
        } else {
          fetchCartAndSync({ forceTotal: true, forceFromState: true });
        }
      });

      observeTargets.forEach((target) => {
        observer.observe(target, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      });
    }
  }

  function boot() {
    if (shouldEnableRepair()) {
      init();
      return;
    }

    let waited = 0;
    const timer = setInterval(() => {
      waited += 100;
      if (shouldEnableRepair()) {
        clearInterval(timer);
        init();
      } else if (waited >= BSS_WAIT_MS) {
        clearInterval(timer);
      }
    }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
